<template>
  <div class="user-table-container">

    <!-- ========== ENCABEZADO ========== -->
    <section class="chat-header">
      <div class="chat-header__text">
        <h1 class="chat-header__title">Usuarios</h1>
        <p class="chat-header__subtitle">Administra los usuarios de la plataforma.</p>
      </div>

      <div class="chat-header__actions">
        <button class="add-contact-btn tooltip" data-tooltip="Añadir Usuario" @click="showCreateUserModal = true">
          <ion-icon name="add-circle-outline" class="icon-add" />
        </button>

        <div class="filter-group">
          <ion-icon name="people-outline" class="select-icon" />
          <select id="role-filter" v-model="selectedRole">
            <option value="">Todos</option>
            <option v-for="role in roles" :key="role" :value="role">
              {{ role }}
            </option>
          </select>
        </div>
      </div>
    </section>

    <!-- ========== CARGANDO ========== -->
    <div v-if="loading" class="loading-spinner">
      <span class="spinner"></span>
      <p>Cargando Usuarios...</p>
    </div>

    <!-- ========== TABLA ========== -->
    <UserTable
      v-if="!loading"
      :users="filteredUsers"
      :current-user-id="currentUserId"
      @edit="editUser"
      @delete="openDeleteModal"
      @link-lines="openManageLinesModal"
    />

    <!-- ========== MODALES ========== -->
    <ManageLinesModal 
      v-if="showManageLinesModal" 
      :user="selectedUserForLines" 
      @close="showManageLinesModal = false"
      @lines-updated="fetchUsers"
    />

    <CreateUserModal 
      v-if="showCreateUserModal" 
      @close="showCreateUserModal = false" 
      @user-created="fetchUsers" 
    />

    <DeleteUserModal
      v-if="showDeleteModal"
      :user="selectedUser"
      @close="showDeleteModal = false"
      @confirm-delete="confirmDelete"
    />

    <EditUserModal
      v-if="showEditUserModal"
      :user="selectedUserToEdit"
      @close="showEditUserModal = false"
      @user-updated="fetchUsers"
    />

  </div>
</template>

<script>
import UserTable from "@/components/users/UserTable.vue";
import CreateUserModal from "@/components/users/CreateUserModal.vue";
import DeleteUserModal from "@/components/users/DeleteUserModal.vue";
import EditUserModal from "@/components/users/EditUserModal.vue";
import ManageLinesModal from "@/components/users/ManageLinesModal.vue"

export default {
  components: { UserTable, CreateUserModal, DeleteUserModal, EditUserModal, ManageLinesModal },
  data() {
    return {
      users: [], 
      roles: ["admin", "agent"], 
      selectedRole: "", 
      showCreateUserModal: false,
      showDeleteModal: false,
      showEditUserModal: false,
      selectedUserToEdit: null,
      selectedUser: null,
      currentUserId: null,
      showManageLinesModal: false,
      selectedUserForLines: null,
      loading: true,
    };
  },
  computed: {
   filteredUsers() {
      let users = this.users;

      if (this.selectedRole) {
        users = users.filter(user => user.role === this.selectedRole);
      }

      return users.sort((a, b) => (a.id === this.currentUserId ? -1 : b.id === this.currentUserId ? 1 : 0));
    },
  },
  methods: {
    getCurrentUserId() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const payload = JSON.parse(atob(token.split(".")[1]));
        this.currentUserId = parseInt(payload.sub, 10);
      } catch (error) {
        console.error("Error al obtener el ID del usuario autenticado:", error);
      }
    },
    async fetchUsers() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/users"); 
        if (response.data && Array.isArray(response.data)) {
          this.users = response.data; 
        } else if (response.data && response.data.data) {
          this.users = response.data.data;
        } else {
          console.error("Formato de respuesta inesperado:", response.data);
        }
      } catch (error) {
        console.error("Error al obtener usuarios:", error);
      } finally {
        this.loading = false; 
      }
    },
    editUser(user) {
      this.selectedUserToEdit = user;
      this.showEditUserModal = true;
    },
    openDeleteModal(user) {
      
      if (!user || !user.id) {
        console.error("Error: Usuario no válido.", user);
        return;
      }
      
      this.selectedUser = user;
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      if (!this.selectedUser || !this.selectedUser.id) {
        console.error("Error: Usuario no seleccionado o ID no válido.");
        return;
      }

      try {
        await this.$axios.delete(`/users/${this.selectedUser.id}`);
        this.fetchUsers(); 
        this.showDeleteModal = false;
        this.selectedUser = null; 
      } catch (error) {
        console.error("Error al eliminar usuario:", error);
      }
    },
    openManageLinesModal(user) {
      this.selectedUserForLines = user;
      this.showManageLinesModal = true;
    },
  },
  mounted() {
    this.fetchUsers(); 
    this.getCurrentUserId();
  },
};
</script>

<style scoped>
.user-table-container {
  padding: 20px;
  background: #f9fafb;
  height: 100vh;
  overflow-y: auto;
}

/* ========== ENCABEZADO ========== */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  margin-bottom: 30px;
  font-family: 'Manrope', sans-serif;
}

.chat-header__text {
  display: flex;
  flex-direction: column;
}

.chat-header__title {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-header__subtitle {
  font-size: 1rem;
  font-weight: 500;
  color: #64748b;
  margin: 0.15rem 0 0 0;
}

.chat-header__actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

/* Botón */
.add-contact-btn {
  background-color: #3b82f6;
  color: white;
  padding: 0.5rem;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.15);
  outline: none;
}

.add-contact-btn:hover {
  background-color: #2563eb;
  box-shadow: 0 6px 10px rgba(37, 99, 235, 0.25);
  transform: scale(1.05);
}

.add-contact-btn:focus {
  box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.6);
}

.icon-add {
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 1;
}

/* Filtro */
.filter-group {
  position: relative;
  display: flex;
  align-items: center;
  width: 130px;
  background-color: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.select-icon {
  position: absolute;
  left: 12px;
  font-size: 1.1rem;
  color: #505050;
  pointer-events: none;
}

.filter-group select {
  width: 100%;
  padding: 0.65rem 1rem 0.65rem 2.4rem;
  border: none;
  border-radius: 0.75rem;
  background-color: transparent;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
}

.filter-group select:focus {
  outline: none;
}

/* -- TOOLTIP --- */
.tooltip {
  position: relative;
}

.tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  top: 125%; 
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background-color: #1e293b;
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 100;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); 
}

.tooltip:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ========== SPINNER DE CARGA ========== */
.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  grid-column: 1 / -1;
  padding: 40px 20px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  color: #666;
  gap: 10px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 5px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s ease-in-out infinite;
  position: relative;
}

.spinner::after {
  content: '';
  position: absolute;
  top: -8px;
  left: -8px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
  animation: pulse 1.6s infinite ease-in-out;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.2;
  }
}

</style>
